import {Box} from '@mui/material';
import AurButton from '../common/aur-button/AurButton';
import {t} from 'i18next';
import AurDrawerNavigation from '../common/aur-drawer-navigation/AurDrawerNavigation';
import AurDrawer from '../common/aur-drawer/AurDrawer';
import {useState} from 'react';
import UploadConciergeReport from './UploadConciergeReport';
import AurPopup from '../common/aur-popup/AurPopup';
import ReportDetails from './ReportDetails';
import UploadConciergeReportFinalStep from './UploadConciergeReportFinalStep';
import {
  RowData,
  UploadDocumentInterface,
} from '../../types/concierge-reports/conciergeReportsType';
import {CONCIERGE_FORM_TYPE} from '../../constants/constants';
import {useDispatch} from 'react-redux';
import {setHandleConciergeReportsCall} from '../../redux/slices/conciergeReportSlice';
import AurAskLioDrawerHeaderButton from '../aur-ask-lio/AurAskLioDrawerHeaderButton';
import {
  GA_EVENT_NAMES,
  GA_CLICK_TYPES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../constants/analyticsConstants';
import {useAnalytics} from '../../hooks/useAnalytics';

type ReportPanelProps = {
  type?: string;
  showDrawer?: boolean;
  selectedReport?: RowData;
  reportNumber?: number;
  totalReports?: number;
  setShowDrawer: (show: boolean) => void;
  handleDetailsNavigation?: (item: number) => void;
};

export const intitialData: UploadDocumentInterface = {
  client: {id: '', name: ''},
  project: {id: '', name: ''},
  teamMembers: [],
  file: new File([''], 'filename'),
  fileName: '',
  description: '',
};

const ReportDetailsPanel = (props: ReportPanelProps) => {
  const {
    setShowDrawer,
    showDrawer,
    selectedReport,
    reportNumber,
    totalReports,
    handleDetailsNavigation,
    type,
  } = props;

  const {trackGAEvent} = useAnalytics();

  const [cancelModal, setCancelModal] = useState<boolean>(false);
  const [uploadFinalStep, setUploadFinalStep] = useState<boolean>(false);
  const [uploadDocumentData, setUploadDocumentData] =
    useState<UploadDocumentInterface>(intitialData);
  const [step, setStep] = useState<string>(CONCIERGE_FORM_TYPE.UPLOAD);

  const handleNavigation = (item: number, isPrevious?: boolean) => {
    if (handleDetailsNavigation) {
      handleDetailsNavigation(item);
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: isPrevious
            ? t('gaTracking.pagination.leftArrow')
            : t('gaTracking.pagination.rightArrow'),
          type: GA_CLICK_TYPES.BUTTON,
          region: GA_REGIONS.DRAWER,
          action: t('gaTracking.reports.drawerCustomReportDetails'),
        },
        subPage: {
          subpage_name: t('gaTracking.reports.customReportDetails'),
          subpage_type: GA_SUBPAGE_TYPE.DRAWER,
        },
      });
    }
  };

  const closeModalHandler = () => {
    setCancelModal(false);
    setShowDrawer(false);
  };

  const closePanelHandler = () => {
    setCancelModal(false);
  };

  const handleDrawerClose = () => {
    type == 'details' ? setShowDrawer(false) : setCancelModal(true);
  };

  const dispatch = useDispatch();

  let contentSection;
  if (type === 'details' && selectedReport) {
    contentSection = <ReportDetails selectedReport={selectedReport} />;
  } else if (uploadFinalStep) {
    contentSection = (
      <Box className="upload-upload-final-container">
        <UploadConciergeReportFinalStep
          uploadDocumentData={uploadDocumentData}
          onCancelClick={() => setCancelModal(true)}
          onEditClick={(description) => {
            setUploadDocumentData({...uploadDocumentData, description});
            setUploadFinalStep(false);
            setStep(CONCIERGE_FORM_TYPE.EDIT);
          }}
          handleUploadSuccess={() => {
            setShowDrawer(false);
            dispatch(setHandleConciergeReportsCall(true));
          }}
        />
      </Box>
    );
  } else {
    contentSection = (
      <UploadConciergeReport
        closePanel={() => setShowDrawer(false)}
        setUploadFinalStep={() => {
          setStep(CONCIERGE_FORM_TYPE.UPLOAD);
          setUploadFinalStep(true);
        }}
        setUploadDocumentData={setUploadDocumentData}
        uploadDocumentData={uploadDocumentData}
        currentStep={step}
      />
    );
  }

  const contentBody = (
    <Box className="report-details-panel">
      <Box className="panel-header">
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <AurButton
            classNames={`drawer-close-button  ${step == CONCIERGE_FORM_TYPE.EDIT ? 'hidden' : ''}`}
            label={t('buttons.close')}
            type="exit"
            size="Medium"
            title={t('buttons.close')}
            onClick={handleDrawerClose}
          />
          <Box
            sx={{
              display: 'flex',
              gap: '24px',
            }}
          >
            <AurAskLioDrawerHeaderButton />
            {type == 'details' ? (
              <AurDrawerNavigation
                previousButtonAriaLabel={t('drawerNavigation.previous')}
                nextButtonAriaLabel={t('drawerNavigation.next')}
                navButtonClass="panel-nav-button"
                aurDrawerNavigationClass="panel-nav-drawer"
                navTypoClass="panel-nav-typo"
                currentItemOrder={reportNumber || 1}
                totalItemCount={totalReports || 1}
                navigationClickHandler={handleNavigation}
              />
            ) : null}
          </Box>
        </Box>
      </Box>
      {contentSection}
    </Box>
  );

  return (
    <>
      <AurDrawer
        className="report-drawer"
        open={showDrawer}
        onClose={handleDrawerClose}
        anchor="right"
        drawerContent={contentBody}
      />
      {cancelModal && (
        <AurPopup
          className="aur-unsaved-changes-popup"
          open={cancelModal}
          onOk={closeModalHandler}
          onCancel={closePanelHandler}
          popupTitle={t('common.cancelUploadPopup.title')}
          popupHeading={t('common.cancelUploadPopup.heading')}
          showPopupHeading={true}
          showPopupTitle={true}
          showCloseIcon={false}
          popupContent={t('common.cancelUploadPopup.content')}
          labelActionCancel={t('common.cancelUploadPopup.buttonStay')}
          labelActionOk={t('common.cancelUploadPopup.buttonYes')}
          exitButtonHint={t('common.cancelUploadPopup.exitButtonHint')}
          continueButtonHint={t('common.cancelUploadPopup.continueButtonHint')}
        />
      )}
    </>
  );
};

export default ReportDetailsPanel;
